import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

// Minimal newsletter signup widget. Writes to Firestore `newsletter_signups`;
// a downstream pipeline (HubSpot upsert + SendGrid welcome drip) picks them up.
// No Cloud Function needed for v1 — keeps the surface tiny and the data
// landed somewhere we control.

function isValidEmail(s) {
  return typeof s === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

const EmailCapture = ({
  source = 'unknown',
  title = 'Get the Weekly Build',
  subtitle = 'One short email each Sunday. No noise.',
}) => {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (state === 'submitting' || state === 'success') return;
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setErrorMsg('That doesn’t look like a valid email.');
      setState('error');
      return;
    }
    setState('submitting');
    setErrorMsg('');
    try {
      await addDoc(collection(db, 'newsletter_signups'), {
        email: trimmed.toLowerCase(),
        source,
        createdAt: serverTimestamp(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        referrer: typeof document !== 'undefined' ? document.referrer : '',
      });
      setState('success');
    } catch (err) {
      setErrorMsg(err?.message || 'Something went wrong — try again in a minute.');
      setState('error');
    }
  };

  if (state === 'success') {
    return (
      <div style={{
        background: '#f0fdf4', border: '1px solid #bbf7d0',
        padding: 20, borderRadius: 12, textAlign: 'center'
      }}>
        <h3 style={{ margin: '0 0 6px', fontSize: '1.1rem', color: '#14532d' }}>You’re on the list.</h3>
        <p style={{ margin: 0, color: '#166534' }}>The next email will land in your inbox on Sunday.</p>
      </div>
    );
  }

  return (
    <div style={{
      background: '#f9fafb', border: '1px solid #e5e7eb',
      padding: 24, borderRadius: 12
    }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '1.15rem', color: '#111827', fontWeight: 700 }}>
        {title}
      </h3>
      <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: '0.95rem' }}>
        {subtitle}
      </p>
      <form onSubmit={onSubmit} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          type="email"
          required
          placeholder="your@email.com"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (state === 'error') setState('idle'); }}
          disabled={state === 'submitting'}
          style={{
            flex: '1 1 220px',
            minWidth: 0,
            padding: '10px 14px',
            border: '1px solid #d1d5db',
            borderRadius: 8,
            fontSize: '0.95rem',
            background: '#fff'
          }}
        />
        <button
          type="submit"
          disabled={state === 'submitting'}
          style={{
            padding: '10px 18px',
            background: state === 'submitting' ? '#93c5fd' : '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: '0.95rem',
            fontWeight: 600,
            cursor: state === 'submitting' ? 'default' : 'pointer'
          }}
        >
          {state === 'submitting' ? 'Subscribing…' : 'Subscribe'}
        </button>
      </form>
      {state === 'error' && (
        <p style={{ margin: '12px 0 0', color: '#991b1b', fontSize: '0.85rem' }}>
          {errorMsg}
        </p>
      )}
      <p style={{ margin: '12px 0 0', color: '#9ca3af', fontSize: '0.75rem' }}>
        We never share or sell email addresses. Unsubscribe anytime.
      </p>
    </div>
  );
};

export default EmailCapture;
